import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { apiRequest } from '../lib/api-client';
import { formatDateTime, formatMoney } from '../ui/format';
import { Badge, ErrorState, Loading, Panel } from '../ui/Page';

/**
 * What a model call costs, and what it is sold for (Phase 140).
 *
 * The screen exists because of a specific trap: the real provider was installed in production and
 * the price list held one row, at zero. Every call would have been metered at nothing and billed
 * at nothing, and the first anybody would have known is the invoice from OpenAI.
 *
 * So two things are deliberate here. **Dollars are what you type** — every provider publishes USD
 * per million tokens, and asking somebody to convert into micro-centavos by hand is asking them to
 * be wrong by a factor of a hundred in a number nobody reads again. And **the result is shown
 * before it is saved**: what the tenant will pay for a million tokens, in reais, computed from the
 * same three numbers the server will store.
 */

interface ModelRate {
  id: string;
  provider: string;
  model: string;
  inputUsdPerMtok: number | null;
  outputUsdPerMtok: number | null;
  brlPerUsd: number | null;
  markupBps: number;
  inputPriceCentsPerMtok: number;
  outputPriceCentsPerMtok: number;
  active: boolean;
  updatedAt: string;
}

interface RateCard {
  rates: ModelRate[];
  unpriced: { provider: string; model: string; calls: number; lastAt: string }[];
}

/** The same arithmetic the server does, so the number on screen is the number that will be saved. */
function priceCents(usd: number, fx: number, markupBps: number): number {
  const microcents = Math.ceil(Math.max(0, usd) * Math.max(0, fx) * 100 * 1_000_000);

  return Math.ceil((microcents * (10_000 + markupBps)) / 10_000 / 1_000_000);
}

export function AiRatesPanel(): React.JSX.Element {
  const client = useQueryClient();
  const card = useQuery({
    queryKey: ['ai-rates'],
    queryFn: () => apiRequest<RateCard>('/admin/ai/rates'),
  });

  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('');
  const [inputUsd, setInputUsd] = useState('');
  const [outputUsd, setOutputUsd] = useState('');
  const [fx, setFx] = useState('');
  const [markup, setMarkup] = useState('30');
  const [error, setError] = useState<string | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['ai-rates'] });
  };

  const save = useMutation({
    mutationFn: () =>
      apiRequest('/admin/ai/rates', {
        method: 'PUT',
        body: {
          provider: provider.trim(),
          model: model.trim(),
          inputUsdPerMtok: Number(inputUsd || 0),
          outputUsdPerMtok: Number(outputUsd || 0),
          brlPerUsd: Number(fx || 0),
          // Percent on screen, basis points on the wire: 30% is 3000, and nobody should have to
          // know that.
          markupBps: Math.round(Number(markup || 0) * 100),
        },
      }),
    onSuccess: () => {
      setError(null);
      setModel('');
      setInputUsd('');
      setOutputUsd('');
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const retire = useMutation({
    mutationFn: (id: string) => apiRequest(`/admin/ai/rates/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (card.isLoading) return <Loading />;
  if (card.isError) return <ErrorState error={card.error} onRetry={() => void card.refetch()} />;

  const usdIn = Number(inputUsd || 0);
  const usdOut = Number(outputUsd || 0);
  const rate = Number(fx || 0);
  const bps = Math.round(Number(markup || 0) * 100);
  const ready = model.trim() !== '' && rate > 0;

  return (
    <Panel title={t('admin.aiRates.title')} description={t('admin.aiRates.description')}>
      {/*
       * The list that makes this screen urgent rather than administrative.
       *
       * A model a call has actually used and no row prices is a call metered at nothing. Named,
       * counted and dated, because "check the rate card" is advice and this is a list.
       */}
      {card.data!.unpriced.length > 0 && (
        <div className="alert alert--warning" role="alert">
          <p className="strong">{t('admin.aiRates.unpriced')}</p>
          <ul>
            {card.data!.unpriced.map((seen) => (
              <li key={`${seen.provider}/${seen.model}`}>
                <code>
                  {seen.provider}/{seen.model}
                </code>{' '}
                — {t('admin.aiRates.unpricedCalls').replace('{n}', String(seen.calls))} ·{' '}
                {formatDateTime(seen.lastAt)}
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => {
                    setProvider(seen.provider);
                    setModel(seen.model);
                  }}
                >
                  {t('admin.aiRates.priceIt')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rate-form">
        <label className="stack__field">
          <span>{t('admin.aiRates.provider')}</span>
          <input
            className="input"
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('admin.aiRates.model')}</span>
          <input
            className="input"
            placeholder="gpt-4o-mini"
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('admin.aiRates.inputUsd')}</span>
          <input
            className="input"
            type="number"
            step="0.0001"
            min={0}
            value={inputUsd}
            onChange={(event) => setInputUsd(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('admin.aiRates.outputUsd')}</span>
          <input
            className="input"
            type="number"
            step="0.0001"
            min={0}
            value={outputUsd}
            onChange={(event) => setOutputUsd(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('admin.aiRates.fx')}</span>
          <input
            className="input"
            type="number"
            step="0.0001"
            min={0}
            placeholder="5,40"
            value={fx}
            onChange={(event) => setFx(event.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('admin.aiRates.markup')}</span>
          <input
            className="input"
            type="number"
            step="1"
            min={0}
            value={markup}
            onChange={(event) => setMarkup(event.target.value)}
          />
        </label>
      </div>

      {/* The result, before anybody presses anything. */}
      {ready && (
        <p className="muted small">
          {t('admin.aiRates.preview')
            .replace('{in}', formatMoney(priceCents(usdIn, rate, bps)))
            .replace('{out}', formatMoney(priceCents(usdOut, rate, bps)))}
        </p>
      )}

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        className="button button--primary"
        disabled={!ready || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t('common.saving') : t('admin.aiRates.save')}
      </button>

      <ul className="rate-list">
        {card.data!.rates.map((row) => (
          <li key={row.id} className={row.active ? '' : 'is-retired'}>
            <span className="cell-stack">
              <span className="strong">
                <code>
                  {row.provider}/{row.model}
                </code>
              </span>
              <span className="muted small">
                {t('admin.aiRates.row')
                  .replace('{in}', formatMoney(row.inputPriceCentsPerMtok))
                  .replace('{out}', formatMoney(row.outputPriceCentsPerMtok))
                  .replace('{markup}', String(row.markupBps / 100))}
                {row.brlPerUsd !== null &&
                  ` · ${t('admin.aiRates.at').replace('{fx}', row.brlPerUsd.toFixed(2))}`}
              </span>
            </span>

            <span className="cell-inline">
              {!row.active && <Badge tone="neutral">{t('admin.aiRates.retired')}</Badge>}

              {row.active && (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => {
                    setProvider(row.provider);
                    setModel(row.model);
                    setInputUsd(row.inputUsdPerMtok === null ? '' : String(row.inputUsdPerMtok));
                    setOutputUsd(row.outputUsdPerMtok === null ? '' : String(row.outputUsdPerMtok));
                    setFx(row.brlPerUsd === null ? '' : String(row.brlPerUsd));
                    setMarkup(String(row.markupBps / 100));
                  }}
                >
                  {t('admin.aiRates.edit')}
                </button>
              )}

              {row.active && (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={retire.isPending}
                  onClick={() => retire.mutate(row.id)}
                >
                  {t('admin.aiRates.retire')}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
